/**
 * The SFOC viewer's time slicer: one window over the date range that moves the
 * measured points and the curves together.
 *
 * Inside the window, the measured points show; at its end, each curve is the
 * version that was in force then (`versionAt`). Drag it, or press Play and it
 * slides across the range - the cloud drifts, and a curve switches when a new
 * version started, at the ticks above the track.
 *
 * Explore's slicer strip (`plot/slicer-strip.ts`) is the model: the same row,
 * histogram track, two-handle band, Play, sweep time and loop, in the same
 * classes. It is not that component because that one slices Explore's chart -
 * its filters, its runtime, its cached response - and this slices the SFOC
 * viewer's own points and curves, through `sfocSlice`.
 */

import { html, nothing, svg, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { fmtTimeShort } from '../plot/render/format.ts';
import { sfocSlice, type TimeSlice } from './sfoc-measured.ts';

const PLAY_GLYPH = svg`<svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><path d="M2 1.2v7.6L8.6 5z" fill="currentColor"/></svg>`;
const PAUSE_GLYPH = svg`<svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><rect x="2" y="1.5" width="2.2" height="7" rx="0.6" fill="currentColor"/><rect x="5.8" y="1.5" width="2.2" height="7" rx="0.6" fill="currentColor"/></svg>`;

const BUCKETS = 48;
const MIN_SHARE = 0.002;
/** Seconds Play takes to sweep the whole range, in the order the chip cycles. */
const SWEEPS = [10, 30, 60, 5];
const WEEK = 7 * 86_400;

/** A version change to mark on the track. */
export interface SliceTick {
  t: number;
  label: string;
}

@customElement('sfoc-time-slicer')
export class SfocTimeSlicer extends LightElement {
  private store = new StoreController(this, [sfocSlice]);

  /** The date range, epoch seconds. */
  @property({ attribute: false }) range: { start: number; end: number } | null = null;
  /** Every loaded point's time, per curve - the histogram under the track. */
  @property({ attribute: false }) times: number[][] = [];
  /** Where curve versions change inside the range. */
  @property({ attribute: false }) ticks: SliceTick[] = [];

  @state() private playing = false;
  @state() private sweep = SWEEPS[0]!;
  @state() private loop = true;
  private frame = 0;

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.stop();
  }

  private get span(): number {
    return this.range ? this.range.end - this.range.start : 0;
  }

  private shares(slice: TimeSlice): [number, number] {
    const r = this.range!;
    const w = this.span;
    const a = slice.min == null ? 0 : Math.min(1, Math.max(0, (slice.min - r.start) / w));
    const b = slice.max == null ? 1 : Math.min(1, Math.max(0, (slice.max - r.start) / w));
    return [a, Math.max(b, a + MIN_SHARE)];
  }

  private set(a: number, b: number): void {
    const r = this.range!;
    const w = this.span;
    // Ends at the range's own edges are stored open, so an untouched side stays
    // "to the edge" when the range moves.
    sfocSlice.set({
      min: a <= 0.0005 ? null : r.start + a * w,
      max: b >= 0.9995 ? null : r.start + b * w,
    });
  }

  private bars(): number[] {
    const r = this.range!;
    const w = this.span;
    const counts = new Array<number>(BUCKETS).fill(0);
    for (const arr of this.times) {
      for (const t of arr) {
        if (!Number.isFinite(t)) continue;
        const i = Math.floor(((t - r.start) / w) * BUCKETS);
        if (i >= 0 && i < BUCKETS) counts[i]!++;
      }
    }
    const top = Math.max(1, ...counts);
    return counts.map((c) => c / top);
  }

  private togglePlay(): void {
    if (this.playing) {
      this.stop();
      return;
    }
    const slice = sfocSlice.value;
    const narrowed = slice.min != null || slice.max != null;
    const [a0, b0] = this.shares(slice);
    // The band is the window. Untouched, it starts as a week - long enough for a
    // cloud, short enough to see it move - or a tenth of a short range.
    let width = b0 - a0;
    let pos = a0;
    if (!narrowed || width >= 0.999) {
      width = this.span >= 3 * WEEK ? WEEK / this.span : 0.1;
      pos = 0;
    }
    if (pos + width >= 0.9995) pos = 0;
    this.playing = true;
    let last = performance.now();
    const step = (now: number): void => {
      if (!this.playing) return;
      const dt = Math.min(100, now - last);
      last = now;
      pos += ((1 - width) * dt) / (this.sweep * 1000);
      if (pos + width >= 1) {
        if (this.loop) pos = 0;
        else {
          this.set(1 - width, 1);
          this.stop();
          return;
        }
      }
      this.set(pos, pos + width);
      this.frame = requestAnimationFrame(step);
    };
    this.set(pos, pos + width);
    this.frame = requestAnimationFrame(step);
  }

  private stop(): void {
    cancelAnimationFrame(this.frame);
    this.playing = false;
  }

  private onDown(e: PointerEvent): void {
    if (e.button !== 0 || !this.range) return;
    this.stop();
    const track = e.currentTarget as HTMLElement;
    const target = e.target as HTMLElement;
    const mode = target.classList.contains('is-lo')
      ? 'lo'
      : target.classList.contains('is-hi')
        ? 'hi'
        : target.classList.contains('slicer-band')
          ? 'pan'
          : 'jump';
    e.preventDefault();
    const rect = track.getBoundingClientRect();
    const along = (ev: PointerEvent): number =>
      Math.min(1, Math.max(0, (ev.clientX - rect.left) / rect.width));
    const [a0, b0] = this.shares(sfocSlice.value);
    const start = along(e);
    try {
      track.setPointerCapture(e.pointerId);
    } catch {
      // A synthetic or already-ended pointer - the listeners below still work.
    }
    track.classList.add('is-dragging');
    let frame = 0;
    const move = (ev: PointerEvent): void => {
      const q = along(ev);
      let a = a0;
      let b = b0;
      if (mode === 'lo') a = Math.min(q, b0 - MIN_SHARE);
      else if (mode === 'hi') b = Math.max(q, a0 + MIN_SHARE);
      else if (mode === 'pan') {
        const d = Math.min(1 - b0, Math.max(-a0, q - start));
        a = a0 + d;
        b = b0 + d;
      } else if (Math.abs(q - a0) < Math.abs(q - b0)) a = Math.min(q, b0 - MIN_SHARE);
      else b = Math.max(q, a0 + MIN_SHARE);
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => this.set(a, b));
    };
    const up = (ev: PointerEvent): void => {
      if (track.hasPointerCapture(ev.pointerId)) track.releasePointerCapture(ev.pointerId);
      track.classList.remove('is-dragging');
      track.removeEventListener('pointermove', move);
      track.removeEventListener('pointerup', up);
      track.removeEventListener('pointercancel', up);
    };
    track.addEventListener('pointermove', move);
    track.addEventListener('pointerup', up);
    track.addEventListener('pointercancel', up);
    if (mode === 'jump') move(e);
  }

  private reset(): void {
    this.stop();
    sfocSlice.set({ min: null, max: null });
  }

  override render(): TemplateResult | typeof nothing {
    const r = this.range;
    if (!r || !(this.span > 0)) return nothing;
    const slice = sfocSlice.value;
    const [a, b] = this.shares(slice);
    const narrowed = slice.min != null || slice.max != null;
    const bars = this.bars();
    const lo = slice.min ?? r.start;
    const hi = slice.max ?? r.end;
    const ticks = this.ticks.filter((t) => t.t > r.start && t.t < r.end);
    return html`<div class="slicer-strip sfoc-slicer">
      <div class="slicer-row ${narrowed ? 'is-narrowed' : ''}">
        <span class="slicer-name" title="Measured points inside the window; curves as they were at its end"
          >Time</span
        >
        <div
          class="slicer-track"
          title="Drag the ends to narrow, the middle to move - double-click to show the whole range"
          @pointerdown=${(e: PointerEvent) => this.onDown(e)}
          @dblclick=${() => this.reset()}
        >
          <div class="slicer-hist" aria-hidden="true">
            ${bars.map((v, i) => {
              const mid = (i + 0.5) / bars.length;
              return html`<span
                class=${mid >= a && mid <= b ? 'is-in' : ''}
                style=${`--fill:${Math.max(0.05, v)}`}
              ></span>`;
            })}
          </div>
          ${ticks.map(
            (t) => html`<span
              class="sfoc-slicer-tick"
              style=${`left:${((t.t - r.start) / this.span) * 100}%`}
              title=${t.label}
            ></span>`,
          )}
          <div class="slicer-band" style=${`left:${a * 100}%;width:${(b - a) * 100}%`}>
            <span class="slicer-handle is-lo"></span>
            <span class="slicer-handle is-hi"></span>
          </div>
        </div>
        <span class="slicer-readout">${fmtTimeShort(lo)} – ${fmtTimeShort(hi)}</span>
        <span class="slicer-play-group">
          <button
            class="slicer-play ${this.playing ? 'is-playing' : ''}"
            title=${this.playing ? 'Pause' : 'Play: slide the window across the date range'}
            aria-label=${this.playing ? 'Pause' : 'Play'}
            @click=${() => this.togglePlay()}
          >
            ${this.playing ? PAUSE_GLYPH : PLAY_GLYPH}
          </button>
          <button
            class="slicer-chip"
            title="Time to sweep the whole range - click to change"
            @click=${() => {
              this.sweep = SWEEPS[(SWEEPS.indexOf(this.sweep) + 1) % SWEEPS.length]!;
            }}
          >
            ${this.sweep}s
          </button>
          <button
            class="slicer-chip ${this.loop ? 'is-on' : ''}"
            title=${this.loop ? 'Looping - click to stop at the end' : 'Stops at the end - click to loop'}
            aria-pressed=${this.loop ? 'true' : 'false'}
            @click=${() => (this.loop = !this.loop)}
          >
            ↻
          </button>
        </span>
      </div>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sfoc-time-slicer': SfocTimeSlicer;
  }
}
